import {
  Cell,
  HeadCell,
  HeadRow,
  Row,
  Table,
} from '@dapp/multi-lib/sdaoui/Table';
import { Body2, Tiny1 } from 'components/Typography';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';
import { useTranslation } from 'react-i18next';
import { VaultIdentity } from 'structure/pv2/vault/utils/vault';
import {
  useVault,
  useVaultAllocation,
} from 'structure/pv2/vault/hooks/useVault';
import { DynaVaultTokenAllocation } from '../../../../api/DynaVaultsAPI';
import { getTokenIconUrl } from 'utils/url';
import { TokenImage } from 'components/TokenImage';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import {
  useDynaLiquidityTokensQuery,
  useVaultTokensQuery,
  ZERO_ADDRESS,
} from '../hooks/useDynavaultManager';
import { useConnection } from 'wagmi';
import { formatUnits } from 'viem';
import isNil from 'lodash/isNil';
import { VaultType } from '../configs/vaultTypes';

interface TableRowProps {
  price: number;
  weight: string;
  balance: string;
  tokenAllocation: DynaVaultTokenAllocation;
}

const TableRow = ({
  price,
  weight,
  tokenAllocation,
  balance,
}: TableRowProps) => {
  const isMobile = useIsMobile();

  return (
    <Row>
      <Cell className={isMobile ? '!px-2 !py-1.5' : ''}>
        <div className='flex flex-row items-center gap-2'>
          <div className='h-[24px] w-[24px]'>
            <TokenImage
              symbol={tokenAllocation.token.symbol}
              src={getTokenIconUrl(tokenAllocation.token.symbol)}
              height='24'
              width='24'
              alt={tokenAllocation.token.name}
            />
          </div>
          {!isMobile && <Body2>{tokenAllocation.token.name}</Body2>}
          <Tiny1 className='text-white-400'>
            {tokenAllocation.token.symbol}
          </Tiny1>
        </div>
      </Cell>
      <Cell className={isMobile ? '!px-2 !py-1.5' : ''}>
        <Body2>{balance}</Body2>
      </Cell>
      <Cell className={isMobile ? '!px-2 !py-1.5' : ''}>
        <Body2>${formatUsd(price)}</Body2>
      </Cell>
      <Cell className={isMobile ? '!px-2 !py-1.5' : ''}>
        <Body2>${formatUsd(parseFloat(tokenAllocation.idleInUSD))}</Body2>
      </Cell>
      <Cell className={isMobile ? '!px-2 !py-1.5' : ''}>
        <Body2>{+weight >= 0 ? `${weight}%` : '-'}</Body2>
      </Cell>
    </Row>
  );
};

interface VaultAllocationTableProps {
  vaultIdentity: VaultIdentity;
}

export const VaultAllocationTable = (props: VaultAllocationTableProps) => {
  const { vaultIdentity } = props;
  const account = useConnection();
  const allocationQuery = useVaultAllocation(vaultIdentity);
  const vaultQuery = useVault(vaultIdentity);

  const { t } = useTranslation('vault');
  const isMobile = useIsMobile();

  const dynaVaultSnapshotQuery = useVaultTokensQuery({
    vaultChainId: vaultIdentity.chainId,
    vaultAddress: vaultIdentity.address,
    from: account.address || ZERO_ADDRESS,
    query: {
      enabled: !!vaultQuery.data && vaultQuery.data?.type === VaultType.YIELD,
    },
  });

  const dynaVaultLiquiditySnapshotQuery = useDynaLiquidityTokensQuery({
    vaultChainId: vaultIdentity.chainId,
    vaultAddress: vaultIdentity.address,
    from: account.address || ZERO_ADDRESS,
    asset: (vaultQuery.data?.asset ?? ZERO_ADDRESS) as `0x${string}`,
    dynaLiquidity: (vaultQuery.data?.metadata?.contracts?.dynaLiquidity ??
      ZERO_ADDRESS) as `0x${string}`,
    query: {
      enabled: !!vaultQuery.data && vaultQuery.data?.type === VaultType.INDEX,
    },
  });

  const snapshotData =
    vaultQuery.data?.type === VaultType.INDEX
      ? dynaVaultLiquiditySnapshotQuery.data
      : dynaVaultSnapshotQuery.data;

  if (
    dynaVaultSnapshotQuery.isLoading ||
    dynaVaultLiquiditySnapshotQuery.isLoading ||
    allocationQuery.isLoading
  ) {
    return <Body2>Loading...</Body2>;
  }

  if (!snapshotData || snapshotData.length === 0) {
    return null;
  }

  const totalAllocation = (allocationQuery.data ?? []).reduce((acc, item) => {
    return acc + parseFloat(item.idleInUSD);
  }, 0);

  const percent = totalAllocation / 100;

  if (isMobile) {
    return (
      <Table role='grid' $gridTemplateColumns='repeat(4, auto) min-content'>
        <HeadRow>
          <HeadCell>{t('VAULT_ALLOCATION_TABLE.TOKEN')}</HeadCell>
          <HeadCell>{t('VAULT_ALLOCATION_TABLE.BALANCE')}</HeadCell>
          <HeadCell>{t('VAULT_ALLOCATION_TABLE.PRICE')}</HeadCell>
          <HeadCell>{t('VAULT_ALLOCATION_TABLE.USD_VALUE')}</HeadCell>
          <HeadCell>
            {t('VAULT_ALLOCATION_TABLE.ALLOCATION_PERCENTAGE')}
          </HeadCell>
        </HeadRow>

        {!isNil(snapshotData) &&
          snapshotData.map((allocation) => {
            const alloc = allocation as typeof allocation & {
              tokenIdle?: bigint;
              tokenDebt?: bigint;
              balance: bigint | string;
            };
            return (
              <TableRow
                key={allocation.symbol}
                price={allocation.price}
                weight={
                  !isNil(allocation.currentWeight)
                    ? allocation.currentWeight.toFixed(2)
                    : (allocation.idleInUsd / percent).toFixed(2)
                }
                tokenAllocation={{
                  idle: alloc.tokenIdle?.toString() ?? '',
                  debt: alloc.tokenDebt?.toString() ?? '',
                  idleInUSD: allocation.idleInUsd.toString(),
                  token: {
                    symbol: allocation.symbol,
                    name: allocation.symbol,
                    decimals: allocation.decimals,
                    address: allocation.tokenAddress as `0x${string}`,
                  },
                  metadata: { weight: allocation.currentWeight.toString() },
                }}
                balance={formatUnits(
                  BigInt(alloc.balance),
                  allocation.decimals
                )}
              />
            );
          })}
      </Table>
    );
  }

  return (
    <Table role='grid' $gridTemplateColumns='repeat(4, auto) max-content'>
      <HeadRow>
        <HeadCell>{t('VAULT_ALLOCATION_TABLE.TOKEN')}</HeadCell>
        <HeadCell>{t('VAULT_ALLOCATION_TABLE.BALANCE')}</HeadCell>
        <HeadCell>{t('VAULT_ALLOCATION_TABLE.PRICE')}</HeadCell>
        <HeadCell>{t('VAULT_ALLOCATION_TABLE.USD_VALUE')}</HeadCell>
        <HeadCell>{t('VAULT_ALLOCATION_TABLE.ALLOCATION_PERCENTAGE')}</HeadCell>
      </HeadRow>
      {!isNil(snapshotData) &&
        snapshotData.map((allocation) => {
          const alloc = allocation as typeof allocation & {
            tokenIdle?: bigint;
            tokenDebt?: bigint;
            balance: bigint | string;
          };
          return (
            <TableRow
              key={allocation.symbol}
              price={allocation.price}
              weight={
                !isNil(allocation.currentWeight)
                  ? allocation.currentWeight.toFixed(2)
                  : (allocation.idleInUsd / percent).toFixed(2)
              }
              tokenAllocation={{
                idle: alloc.tokenIdle?.toString() ?? '',
                debt: alloc.tokenDebt?.toString() ?? '',
                idleInUSD: allocation.idleInUsd.toString(),
                token: {
                  symbol: allocation.symbol,
                  name: allocation.symbol,
                  decimals: allocation.decimals,
                  address: allocation.tokenAddress as `0x${string}`,
                },
                metadata: { weight: allocation.currentWeight.toString() },
              }}
              balance={formatUnits(BigInt(alloc.balance), allocation.decimals)}
            />
          );
        })}
    </Table>
  );
};
